import React, { useMemo, useState } from "react";
import {
  Modal,
  Button,
  ListGroup,
  Form,
  Accordion,
  Badge,
} from "react-bootstrap";
import { FaCog } from "react-icons/fa";
import {
  AlertCircle,
  CreditCard,
  DollarSign,
  Edit,
  Mail,
  RefreshCw,
  Server,
  Settings,
  ShieldOff,
  Shield,
  Smartphone,
  Truck,
  UploadCloud,
  Users,
  Zap,
} from "react-feather";
import { ConfirmOverlay } from "./ConfirmOverlay";

export default function ClientActionsMenu({
  client,
  user,
  activeUser,
  impersonationUser,
  t,
  // callbacks coming from parent (so business logic stays where it is now)
  onEdit,
  onDelete,
  onBilling,
  onWhitelabel,
  onUpgradeRole,
  onAssignOffers,
  onAssignPermissions,
  onImpersonate,
  onSendWelcomeEmail,
  onResetPassword,
  onRegisterPayment,
  onCreditNote,
  onInvoiceCycle,
  onInitSims,
  onInitAlerts,
  on1nceConfig,
  onShipSims,
  onDeallocateSims,
  onSuspend,
  onReinstate,
}) {
  const [show, setShow] = useState(false);
  const [query, setQuery] = useState("");

  const [confirmOpen, setConfirmOpen] = useState(false);
  const [pendingAction, setPendingAction] = useState(null);

  const trigger = (action) => {
    setPendingAction(action);
    setConfirmOpen(true);
  };

  const can = useMemo(() => {
    const role = user?.role?.toUpperCase?.() || user?.privilege || "";
    return {
      isAdmin: role === "ADMIN",
      isTenant: role === "TENANT",
      isManager: role === "MANAGER",
      isSubaccount: role === "SUBACCOUNT",
      notImpersonating: !impersonationUser?.id,
    };
  }, [user, impersonationUser]);

  // helper: simple match against title + description for search
  const match = (text) =>
    !query || (text ?? "").toLowerCase().includes(query.toLowerCase());

  // CATEGORY DEFINITIONS ----------------------------------------------------
  const baseActions = [
    {
      key: "edit",
      title: t("crm.edit"),
      description: t("crm.edit") + " profile",
      icon: <Edit size={16} />,
      visible: client.privilege !== "ADMIN",
      onClick: onEdit,
    },
    {
      key: "role",
      title: t("crm.role"),
      description: "Upgrade or change user role",
      icon: <Users size={16} />,
      visible:
        (activeUser.role === "admin" || activeUser.role === "tenant") &&
        client.privilege !== "SUBACCOUNT" &&
        client.privilege !== "ADMIN",
      onClick: onUpgradeRole,
    },
    {
      key: "permissions",
      title: t("crm.permissions"),
      description: "Manage subaccount permissions",
      icon: <Shield size={16} />,
      visible:
        client.privilege === "SUBACCOUNT" && activeUser.role !== "subaccount",
      onClick: onAssignPermissions,
    },
    {
      key: "offers",
      title: t("crm.offers"),
      description: "Assign or revoke offers",
      icon: <Zap size={16} />,
      visible: client.privilege !== "ADMIN" && activeUser.role !== "subaccount",
      onClick: onAssignOffers,
    },
    {
      key: "billing",
      title: t("crm.billing"),
      description: "Payment methods & preferences",
      icon: <CreditCard size={16} />,
      visible: ["TENANT", "MANAGER", "CUSTOMER"].includes(client.privilege),
      onClick: onBilling,
    },
    {
      key: "whitelabel",
      title: t("crm.whitelabeling"),
      description: "Branding & domain settings",
      icon: <Settings size={16} />,
      visible:
        can.isAdmin &&
        client.privilege !== "SUBACCOUNT" &&
        client.privilege !== "ADMIN",
      onClick: onWhitelabel,
    },
    {
      key: "welcome",
      title: t("crm.sendWelcomeEmail"),
      description: "Send onboarding email",
      icon: <Mail size={16} />,
      visible: can.isAdmin,
      onClick: onSendWelcomeEmail,
      confirm: {
        title: "Send Welcome Email?",
        message:
          "The user will receive a welcome e-mail with instructions to change the password",
        variant: "warning",
        confirmLabel: "Confirm",
      },
    },
    {
      key: "reset",
      title: t("crm.resetPassword"),
      description: "Trigger password reset email",
      icon: <RefreshCw size={16} />,
      visible: true,
      onClick: onResetPassword,
      confirm: {
        title: "Reset Password?",
        message:
          "The user will receive an e-mail with instructions to change the password",
        variant: "warning",
        confirmLabel: "Suspend",
      },
    },
    {
      key: "impersonate",
      title: t("crm.impersonate"),
      description: "Browse the app as this user",
      icon: <Users size={16} />,
      visible:
        (can.isAdmin ||
          can.isTenant ||
          can.isManager ||
          (can.isSubaccount &&
            (user?.admin || user?.tenant || user?.manager))) &&
        can.notImpersonating,
      onClick: onImpersonate,
      confirm: {
        title: "Impersonate this account?",
        message:
          "You will see content as if you are logged in as the selected user.",
        variant: "warning",
        confirmLabel: "Impersonate",
      },
    },
  ];

  const simAndAlerts = [
    {
      key: "initSims",
      title: t("crm.initializeSIMs"),
      description:
        "Call Velos API to synchronize the SIM statuses for this portfolio",
      icon: <Smartphone size={16} />,
      visible:
        can.isAdmin &&
        client.privilege !== "SUBACCOUNT" &&
        client.privilege !== "ADMIN",
      onClick: onInitSims,
      confirm: {
        title: "Initialize SIMs?",
        message:
          "SIM statuses for this portfolio will be synced accoding to Velos API. This only applies for Velos SIMs",
        variant: "warning",
        confirmLabel: "Confirm",
      },
    },
    {
      key: "oneNCE",
      title: t("crm.1nceModal.title"),
      description: "Configure 1NCE integration",
      icon: <Server size={16} />,
      visible:
        can.isAdmin &&
        client.privilege !== "SUBACCOUNT" &&
        client.privilege !== "ADMIN",
      onClick: on1nceConfig,
    },
    {
      key: "shipSims",
      title: "Ship SIMs",
      description: "Ship SIMs to selected account",
      icon: <Truck size={16} />,
      visible:
        client.privilege !== "SUBACCOUNT" && client.privilege !== "ADMIN",
      onClick: onShipSims,
    },
    {
      key: "deallocateSims",
      title: "Deallocate SIMs",
      description: "Remove SIMs from selected account",
      icon: <UploadCloud size={16} />,
      visible:
        client.privilege !== "SUBACCOUNT" && client.privilege !== "ADMIN",
      onClick: onDeallocateSims,
    },
  ];

  const finance = [
    {
      key: "registerPayment",
      title: t("crm.registerPayment.title"),
      description: "Log incoming payment",
      icon: <DollarSign size={16} />,
      visible:
        can.isAdmin &&
        client.privilege !== "SUBACCOUNT" &&
        client.privilege !== "ADMIN",
      onClick: onRegisterPayment,
    },
    {
      key: "creditNote",
      title: t("crm.creditNote.title"),
      description: "Issue a credit note",
      icon: <DollarSign size={16} />,
      visible:
        can.isAdmin &&
        client.privilege !== "SUBACCOUNT" &&
        client.privilege !== "ADMIN",
      onClick: onCreditNote,
    },
    {
      key: "invoiceCycle",
      title: t("crm.invoiceCycle.title"),
      description: "Trigger invoice generation",
      icon: <DollarSign size={16} />,
      visible:
        can.isAdmin &&
        client.privilege !== "SUBACCOUNT" &&
        client.privilege !== "ADMIN",
      onClick: onInvoiceCycle,
      confirm: {
        title: "Run Invoice Cycle?",
        message:
          "Current invoice will be recalculated. This may cause inconsistent invoice data and amounts.",
        variant: "warning",
        confirmLabel: "Confirm",
      },
    },
  ];

  const status = [
    {
      key: "suspend",
      title: "Suspend",
      description: "Temporarily disable access",
      icon: <ShieldOff size={16} />,
      visible: client.privilege !== "ADMIN" && !client.isSuspended,
      onClick: onSuspend,
      confirm: {
        title: "Suspend this account?",
        message: "The user will be unable to sign in until reinstated.",
        variant: "warning",
        confirmLabel: "Suspend",
      },
      badge: client.isSuspended ? null : (
        <Badge bg="warning" pill>
          New
        </Badge>
      ),
    },
    {
      key: "reinstate",
      title: "Reinstate",
      description: "Restore access",
      icon: <Shield size={16} />,
      visible: client.privilege !== "ADMIN" && client.isSuspended,
      onClick: onReinstate,
      confirm: {
        title: "Reinstate this account?",
        message: "Access will be restored immediately.",
        variant: "primary",
        confirmLabel: "Reinstate",
      },
    },
  ];

  const danger = [
    {
      key: "delete",
      title: t("crm.delete"),
      description: "Remove user and all related data",
      icon: <AlertCircle size={16} />,
      visible: true,
      onClick: onDelete,
      danger: true,
      confirm: {
        title: "Delete this user?",
        message:
          "This action cannot be undone. All related data will be permanently removed.",
        variant: "danger",
        confirmLabel: "Delete permanently",
        // require typing the client’s full name to avoid mistakes
        requireText: client.fullName,
        requireTextLabel: `To confirm, type the user's full name`,
      },
    },
  ];

  const sections = [
    { id: "profile", title: "Profile", items: baseActions },
    { id: "sims", title: "SIMs & Alerts", items: simAndAlerts },
    { id: "finance", title: "Billing & Finance", items: finance },
    { id: "status", title: "Status", items: status },
    { id: "danger", title: "Danger Zone", items: danger },
  ];

  const filteredSections = sections.map((s) => ({
    ...s,
    items: s.items.filter(
      (a) => a.visible && (match(a.title) || match(a.description))
    ),
  }));

  const totalCount = filteredSections.reduce(
    (acc, s) => acc + s.items.length,
    0
  );

  return (
    <>
      <Button
        variant="light"
        size="sm"
        className="d-inline-flex align-items-center gap-2"
        onClick={() => setShow(true)}
      >
        <FaCog size={16} />
        <span>Actions</span>
      </Button>

      <Modal
        show={show}
        onHide={() => setShow(false)}
        centered
        fullscreen="sm-down"
      >
        <Modal.Header closeButton>
          <Modal.Title>
            {client.fullName} — Actions
            <Badge bg="secondary" className="ms-2">
              {totalCount}
            </Badge>
          </Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <Form.Control
            placeholder="Search actions…"
            className="mb-3"
            value={query}
            onChange={(e) => setQuery(e.target.value)}
          />

          <Accordion alwaysOpen>
            {filteredSections.map((section, idx) => {
              const isDanger = section.id === "danger";
              const defaultOpen = !["sims", "finance", "danger"].includes(
                section.id
              );
              if (section.items.length === 0) return null;
              return (
                <Accordion.Item
                  eventKey={String(idx)}
                  key={section.id}
                  defaultChecked={defaultOpen}
                >
                  <Accordion.Header>
                    <span className={isDanger ? "text-danger" : ""}>
                      {section.title}
                    </span>
                    <Badge bg={isDanger ? "danger" : "light"} className="ms-2">
                      {section.items.length}
                    </Badge>
                  </Accordion.Header>
                  <Accordion.Body>
                    <ListGroup variant="flush">
                      {section.items.map((a) => (
                        <ListGroup.Item
                          key={a.key}
                          action
                          onClick={() => {
                            // setShow(false);
                            // a.onClick?.();
                            const needsConfirm = (a) => !!a.confirm;
                            if (needsConfirm(a)) {
                              trigger(a); // show ConfirmOverlay
                            } else {
                              setShow(false); // close the actions modal
                              a.onClick?.(); // run the action immediately
                            }
                          }}
                          className={`d-flex align-items-center justify-content-between ${
                            a.danger ? "list-group-item-danger" : ""
                          }`}
                        >
                          <div className="d-flex align-items-center gap-3">
                            {a.icon}
                            <div>
                              <div
                                className={`fw-semibold ${
                                  a.danger ? "text-danger" : ""
                                }`}
                              >
                                {a.title}
                              </div>
                              {a.description && (
                                <div className="text-muted small">
                                  {a.description}
                                </div>
                              )}
                            </div>
                          </div>
                          {a.badge}
                        </ListGroup.Item>
                      ))}
                    </ListGroup>
                  </Accordion.Body>
                </Accordion.Item>
              );
            })}
          </Accordion>

          {pendingAction && (
            <ConfirmOverlay
              show={confirmOpen}
              onCancel={() => {
                setConfirmOpen(false);
                setPendingAction(null);
              }}
              onHide={() => {
                setConfirmOpen(false);
                setPendingAction(null);
              }}
              onConfirm={() => {
                // Close the menu after confirmation to provide a sense of completion
                setShow(false);
                pendingAction.onClick?.();
                setPendingAction(null);
              }}
              title={
                pendingAction.confirm?.title || `Confirm ${pendingAction.title}`
              }
              message={pendingAction.confirm?.message}
              variant={
                pendingAction.confirm?.variant ||
                (pendingAction.danger ? "danger" : "primary")
              }
              confirmLabel={pendingAction.confirm?.confirmLabel || "Confirm"}
              requireText={pendingAction.confirm?.requireText}
              requireTextLabel={pendingAction.confirm?.requireTextLabel}
              meta={{
                User: client.fullName,
                Action: pendingAction.title,
              }}
            />
          )}

          {totalCount === 0 && (
            <div className="text-center text-muted py-5">
              No actions available
            </div>
          )}
        </Modal.Body>
      </Modal>
    </>
  );
}
